// Floating "Text us" launcher + lead capture panel for the Editorial Warm
// surface. The launcher chrome is kept from the original design; the panel
// now shows a simple phone + reason form that posts to /api/v1/leads instead
// of opening the AI chat backend.

import { useEffect, useRef, useState } from 'react'
import { apiClient } from '../../services/api'
import { trackTextUsConversion } from '../../utils/analytics'
import { NalaIcon } from './NalaIcon'
import { nala } from './palette'

export default function SmsChat() {
  const [open, setOpen] = useState(false)
  const [phone, setPhone] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onOpen = () => setOpen(true)
    window.addEventListener('nala-open-sms', onOpen)
    return () => window.removeEventListener('nala-open-sms', onOpen)
  }, [])

  // Reset form state when panel is closed
  useEffect(() => {
    if (!open) {
      if (timerRef.current) clearTimeout(timerRef.current)
      setPhone('')
      setReason('')
      setSubmitting(false)
      setSubmitted(false)
      setError('')
    }
  }, [open])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await apiClient.post('/leads', {
        lead: {
          phone,
          notes: reason,
          source: 'website_text_us',
          stage: 'pre_lead',
        },
      })
      setSubmitted(true)
      // Push the "Text us" conversion into the GTM dataLayer so the
      // container can fire the Google Ads / GA4 conversion tag.
      trackTextUsConversion({ source: 'website_text_us' })
      // Legacy direct pixels (kept for any tags not yet migrated to GTM).
      try {
        ;(window as any).fbq?.('track', 'Lead', { content_name: 'text_us' })
      } catch {}
      timerRef.current = setTimeout(() => setOpen(false), 3000)
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? 'Close chat' : 'Text us'}
        className="fixed right-6 bottom-6 z-[9000] inline-flex items-center gap-2.5 rounded-full px-5 py-3.5 font-sans text-sm font-semibold transition-transform hover:-translate-y-0.5"
        style={{
          background: nala.accent,
          color: nala.accentInk,
          boxShadow: '0 14px 40px rgba(40,30,20,0.28)',
        }}
      >
        <span className="relative inline-flex">
          <NalaIcon.Phone size={16} color={nala.accentInk} />
          <span
            className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full"
            style={{
              background: '#7fcf9a',
              boxShadow: `0 0 0 2px ${nala.accent}`,
            }}
          />
        </span>
        {open ? 'Close chat' : 'Text us'}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed right-6 bottom-24 z-[9000] flex flex-col overflow-hidden"
          style={{
            width: 380,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 120px)',
            background: nala.surface,
            borderRadius: 18,
            border: `1px solid ${nala.line}`,
            boxShadow: '0 30px 80px rgba(20,14,8,0.35)',
            color: nala.ink,
            fontFamily: nala.sans,
          }}
        >
          {/* Panel header */}
          <div
            className="flex items-center gap-3 px-4 py-3 shrink-0"
            style={{ borderBottom: `1px solid ${nala.line}` }}
          >
            <div className="relative">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full font-bold"
                style={{
                  background: nala.accent,
                  color: nala.accentInk,
                  fontFamily: nala.display,
                }}
              >
                S
              </div>
              <span
                className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full"
                style={{
                  background: '#7fcf9a',
                  border: `2px solid ${nala.surface}`,
                }}
              />
            </div>
            <div className="flex-1">
              <div
                className="text-sm font-semibold"
                style={{ fontFamily: nala.display }}
              >
                Text us
              </div>
              <div
                className="flex items-center gap-1.5 text-xs"
                style={{ color: nala.mute }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: nala.ok }}
                />
                Typically replies in under 5 min
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="text-xl leading-none"
              style={{ color: nala.mute }}
            >
              ×
            </button>
          </div>

          {/* Panel body */}
          <div className="flex-1 min-h-0 px-5 py-5" style={{ background: nala.surface }}>
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-8 text-center gap-3">
                <div
                  className="text-2xl font-semibold"
                  style={{ fontFamily: nala.display, color: nala.ink }}
                >
                  We'll text you shortly! 🐾
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm mb-4" style={{ color: nala.mute }}>
                  Enter your details and we'll text you right away.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="tel"
                      required
                      placeholder="+1 (555) 000-0000"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 text-sm outline-none transition"
                      style={{
                        borderColor: nala.line,
                        background: '#fff',
                        color: nala.ink,
                        fontFamily: 'inherit',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = nala.accent)}
                      onBlur={(e) => (e.target.style.borderColor = nala.line)}
                    />
                  </div>
                  <div>
                    <textarea
                      required
                      placeholder="What brings you in?"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full rounded-xl border px-4 py-3 text-sm outline-none transition resize-none"
                      style={{
                        borderColor: nala.line,
                        background: '#fff',
                        color: nala.ink,
                        fontFamily: 'inherit',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = nala.accent)}
                      onBlur={(e) => (e.target.style.borderColor = nala.line)}
                    />
                  </div>
                  {error && (
                    <p className="text-sm" style={{ color: nala.clay }}>
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-full py-3 text-sm font-semibold transition"
                    style={{
                      background: submitting ? nala.line : nala.accent,
                      color: nala.accentInk,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {submitting ? 'Sending…' : 'Send'}
                  </button>
                </form>
              </>
            )}
          </div>

          <div
            className="px-4 py-2.5 text-center text-[11px] leading-relaxed shrink-0"
            style={{
              color: nala.mute,
              background: nala.surface,
              borderTop: `1px solid ${nala.line}`,
            }}
          >
            By submitting, you agree to receive SMS from Nala about appointment
            scheduling, confirmations, reminders, and care updates at the number
            provided. Consent is not a condition of purchase. Msg frequency
            varies; msg &amp; data rates may apply. Reply STOP to opt out, HELP
            for help. See our{' '}
            <a href="/policy" style={{ color: nala.mute, textDecoration: 'underline' }}>
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/terms" style={{ color: nala.mute, textDecoration: 'underline' }}>
              Terms
            </a>
            .
          </div>
        </div>
      )}
    </>
  )
}
